import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';

import { CustomerCreateModal } from './CustomerCreateModal.jsx';

const STAGES = ['LEAD', 'QUALIFY', 'PROPOSAL', 'NEGOTIATE'];
const EMPTY = { title: '', amount: '0', stage: 'LEAD', customerId: '', expectedCloseDate: '' };

export function OpportunityCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);

  const customers = useQuery({
    queryKey: ['sales', 'customers', 'all'],
    queryFn: () => salesApi.listCustomers().then((r) => r.items || []),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: (body) => salesApi.createOpportunity(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'opportunities'] });
      setForm(EMPTY);
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function handleField(key) {
    return (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  }

  function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    create.mutate({
      title: form.title.trim(),
      amount: form.amount || '0',
      stage: form.stage,
      customerId: form.customerId || undefined,
      expectedCloseDate: form.expectedCloseDate || undefined,
    });
  }

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={t('sales.pipeline.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="modal-body" style={{ padding: 0, gap: 'var(--space-3)' }}>
        <label className="field">
          <span className="field-label">{t('sales.pipeline.modal.titleField')}</span>
          <input className="input" value={form.title} onChange={handleField('title')} required />
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('sales.pipeline.modal.amount')}</span>
            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={handleField('amount')}
            />
          </label>
          <label className="field">
            <span className="field-label">{t('sales.pipeline.modal.stage')}</span>
            <select className="select" value={form.stage} onChange={handleField('stage')}>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {t(`sales.pipeline.stage.${s}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('sales.pipeline.modal.customer')}</span>
            <div className="inline-create">
              <select className="select" value={form.customerId} onChange={handleField('customerId')}>
                <option value="">{t('sales.pipeline.modal.customer.none')}</option>
                {(customers.data || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <Button type="button" variant="ghost" className="inline-create-btn" onClick={() => setCustomerModalOpen(true)}>
                + {t('common.new')}
              </Button>
            </div>
          </label>
          <label className="field">
            <span className="field-label">{t('sales.pipeline.modal.close')}</span>
            <input
              className="input"
              type="date"
              value={form.expectedCloseDate}
              onChange={handleField('expectedCloseDate')}
            />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
    <CustomerCreateModal
      open={customerModalOpen}
      onClose={() => setCustomerModalOpen(false)}
      onCreated={(created) => {
        queryClient.invalidateQueries({ queryKey: ['sales', 'customers', 'all'] });
        if (created?.id) {
          setForm((p) => ({ ...p, customerId: created.id }));
        }
      }}
    />
    </>
  );
}
